import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import * as authService from '../services/authService';
import type { LoginCredentials, RegisterData } from '../services/authService';
import { setGlobalTokenExpirationHandler } from '../utils/apiUtils';

interface User {
  id: number; // Or string, depending on your user ID type
  username: string;
  email?: string; // Optional, depending on what your backend returns/what you need
  is_admin: boolean; // <-- Added is_admin
  is_read_only: boolean;
  // Add other user properties as needed
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  is_admin: boolean; // <-- Added is_admin directly for convenience
  is_read_only: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  // forgotPassword: (email: string) => Promise<void>; // Add if you want to manage from context
  // resetPassword: (token: string, password: string) => Promise<void>; // Add if you want to manage from context
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start true to check initial auth status
  const [isAdminState, setIsAdminState] = useState<boolean>(false); // Local state for isAdmin
  const [isReadOnlyState, setIsReadOnlyState] = useState<boolean>(false);
  const tokenExpiryTimerRef = useRef<number | null>(null);

  const clearTokenExpiryTimer = useCallback(() => {
    if (tokenExpiryTimerRef.current) {
      window.clearTimeout(tokenExpiryTimerRef.current);
      tokenExpiryTimerRef.current = null;
    }
  }, []);

  const getTokenExpiryMs = useCallback((jwtToken: string): number | null => {
    try {
      const parts = jwtToken.split('.');
      if (parts.length !== 3) return null;
      const base64Payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64Payload));
      if (!payload?.exp || typeof payload.exp !== 'number') return null;
      return payload.exp * 1000;
    } catch (error) {
      console.warn('Failed to parse token expiration from JWT payload', error);
      return null;
    }
  }, []);

  const scheduleTokenExpiryLogout = useCallback((jwtToken: string) => {
    clearTokenExpiryTimer();
    const expiryMs = getTokenExpiryMs(jwtToken);
    if (!expiryMs) return;
    const msUntilExpiry = expiryMs - Date.now();
    if (msUntilExpiry <= 0) {
      setUser(null);
      setToken(null);
      setIsAdminState(false);
      setIsReadOnlyState(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      return;
    }
    tokenExpiryTimerRef.current = window.setTimeout(() => {
      setUser(null);
      setToken(null);
      setIsAdminState(false);
      setIsReadOnlyState(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    }, msUntilExpiry);
  }, [clearTokenExpiryTimer, getTokenExpiryMs]);

  // Logout function that clears all auth state
  const logout = useCallback(() => {
    clearTokenExpiryTimer();
    setUser(null);
    setToken(null);
    setIsAdminState(false);
    setIsReadOnlyState(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    // TODO: Optionally, call a backend /logout endpoint to invalidate the token server-side if needed.
  }, [clearTokenExpiryTimer]);

  // Set up token expiration handlers
  useEffect(() => {
    // Set up both handlers for backward compatibility
    authService.setTokenExpirationHandler(() => {
      logout();
    });

    setGlobalTokenExpirationHandler(() => {
      logout();
    });
  }, [logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        const expiryMs = getTokenExpiryMs(storedToken);
        if (expiryMs && expiryMs <= Date.now()) {
          console.warn('Stored token has expired, clearing auth state');
          logout();
          setIsLoading(false);
          return;
        }

        setToken(storedToken);
        scheduleTokenExpiryLogout(storedToken);
        // TODO: Optionally, verify token with backend here and fetch user details
        // For now, if a token exists, we assume it might be valid and try to get user info
        // or decode it to get basic user info if the token contains it.
        // This part depends on how your JWT is structured and if you have a /me endpoint.
        // A better approach: call an endpoint like /api/auth/me to get current user with the token.
        try {
          // Example: Decode token (if it contains user info directly and you trust it without re-verification on every load)
          // const decodedUser = jwtDecode(storedToken); // You'd need a jwt-decode library
          // setUser(decodedUser); 
          // OR: Fetch user from a /me endpoint using the authService
          // const currentUser = await authService.getMyProfile(); // You would need to implement getMyProfile
          // setUser(currentUser);
          // For now, we don't have a /me endpoint or direct user info in token for this example.
          // We'll set a placeholder user if token exists, real user info comes from login response.
          const storedUser = localStorage.getItem('authUser');
          if (storedUser) {
            const parsedUser: User = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAdminState(parsedUser.is_admin || false); // Set admin state from stored user
            setIsReadOnlyState(parsedUser.is_read_only || false);
          } else {
            // If we have a token but no user data, something's wrong
            console.warn('Token found but no user data, clearing auth state');
            logout();
          }
        } catch (error) {
          console.error('Failed to initialize user from token', error);
          logout();
        }
      }
      setIsLoading(false);
    };
    initializeAuth();
    return () => {
      clearTokenExpiryTimer();
    };
  }, [logout, getTokenExpiryMs, scheduleTokenExpiryLogout, clearTokenExpiryTimer]);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials); // response now includes is_admin
      const userData: User = { 
        id: response.userId, 
        username: response.username, 
        is_admin: response.is_admin || false,
        is_read_only: response.is_read_only || false
      };
      setUser(userData);
      setToken(response.token);
      setIsAdminState(userData.is_admin);
      setIsReadOnlyState(userData.is_read_only);
      scheduleTokenExpiryLogout(response.token);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('authUser', JSON.stringify(userData));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Login failed:', error);
      throw error; // Re-throw to be handled by the calling component
    }
  };

  const register = async (userData: RegisterData) => {
    setIsLoading(true);
    try {
      // authService.register might not return a token or log the user in directly.
      // Adjust based on your backend's /register behavior.
      // If it logs in: handle similarly to login().
      // If not: just show success message, user then needs to login.
      await authService.register(userData);
      // For this example, registration doesn't auto-login. User needs to login after.
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Registration failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isLoading,
      isAuthenticated: !!token && !!user, // More robust check
      is_admin: isAdminState, // Use the local state
      is_read_only: isReadOnlyState,
      login,
      register,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
